import { useSearchAnything } from "../../hooks"
import SearchResultItem from "./result-item"
import If from "../if"
import SingleRowListSlim from "../skeletons/single-row-list-slim"
import RecentSearches from "./recent-searches"
import PageSearchForm from "./page-search-form"
import { useEffect, useRef, useState, useMemo } from "react"
import { classNames } from "../../utils/classnames"

interface Props {
  handleMasquerade: (loid: string) => void
  isAdmin: boolean
}

const SearchAnything = ({ isAdmin, handleMasquerade }: Props) => {
  const listboxId = "page-search-listbox"
  const {
    isOpen,
    isLoadingResults,
    query,
    setQuery,
    searchResults,
    recentSearchesData,
    open,
    close,
    onFocusInput,
    onClickItem,
    onClickResetRecentSearches
  } = useSearchAnything()

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  // Derive items array for navigation
  const items = useMemo(() => {
    if (query?.length >= 3) {
      return searchResults
    }
    // For recent searches, add 1 to count for the Clear button
    return recentSearchesData
  }, [query, searchResults, recentSearchesData])

  // Total navigable items (including Clear button for recent searches)
  const totalNavigableItems = useMemo(() => {
    if (query?.length >= 3) {
      return searchResults.length
    }
    // Add 1 for Clear button if recent searches exist
    return recentSearchesData.length > 0 ? recentSearchesData.length + 1 : 0
  }, [query, searchResults.length, recentSearchesData.length])

  // Reset active index when items change or dropdown closes
  useEffect(() => {
    setActiveIndex(-1)
  }, [items, isOpen])

  // Focus management for active item
  useEffect(() => {
    if (activeIndex >= 0 && activeIndex < totalNavigableItems) {
      itemRefs.current[activeIndex]?.focus()
      itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" })
    }
  }, [activeIndex, totalNavigableItems])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        close()
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen, close])

  // Handle Escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        close()
        inputRef.current?.blur()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, close])

  // Global Cmd/Ctrl+K handler
  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        open()
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
      }
    }

    document.addEventListener("keydown", handleGlobalShortcut)
    return () => document.removeEventListener("keydown", handleGlobalShortcut)
  }, [open])

  // Handle arrow navigation on input
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && totalNavigableItems > 0) {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === "Tab") {
      // Close dropdown and allow Tab to move focus naturally
      close()
    }
  }

  // Handle arrow navigation on items
  const handleItemKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (index < totalNavigableItems - 1) {
        setActiveIndex(index + 1)
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (index === 0) {
        setActiveIndex(-1)
        inputRef.current?.focus()
      } else {
        setActiveIndex(index - 1)
      }
    } else if (event.key === "Enter") {
      event.preventDefault()

      // For recent searches, index 0 is the Clear button (handled in RecentSearches)
      // For search results, select the item directly
      if (query?.length >= 3) {
        onClickItem(items[index])
      } else {
        // For recent searches, indices start at 1 for actual items
        if (index > 0) {
          onClickItem(items[index - 1])
        }
        // Index 0 (Clear button) is handled in RecentSearches component
      }
    } else if (event.key === "Tab") {
      // Close dropdown and allow Tab to move focus naturally
      close()
      // Don't prevent default - let Tab work normally
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full flex items-center text-gray-400 focus-within:text-gray-600 sm:max-w-[calc(42rem*0.75)]"
    >
      <PageSearchForm
        ref={inputRef}
        query={query}
        setQuery={setQuery}
        onFocus={onFocusInput}
        onKeyDown={handleInputKeyDown}
        controlsId={listboxId}
        isOpen={isOpen}
      />

      <div
        id={listboxId}
        aria-hidden={!isOpen}
        role="listbox"
        className={classNames(
          // size
          "w-[95vw] sm:w-full",
          // layout
          "absolute top-full z-50 overflow-hidden",
          // spacing
          "mt-2 sm:-mt-1.5",
          // position (keep user-specified mobile centering)
          "left-1/3 -translate-x-1/3 sm:left-0 sm:translate-x-0",
          // border
          "border border-neutral-200 rounded-lg",
          // background
          "bg-white",
          // effects
          "shadow-2xl transition duration-200 ease-out transform-gpu",
          // states
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-3 pointer-events-none"
        )}
      >
        <div className="max-h-80 overflow-auto">
          {searchResults.length ? (
            <>
              {searchResults.map((result, idx) => (
                <SearchResultItem
                  key={`${result.id}-${result.item}-${idx}`}
                  ref={el => (itemRefs.current[idx] = el)}
                  isAdmin={isAdmin}
                  isFirstItem={
                    !idx || result.type !== searchResults[idx - 1].type
                  }
                  isActive={activeIndex === idx}
                  data={result}
                  onClickItem={onClickItem}
                  handleMasquerade={handleMasquerade}
                  closeModal={close}
                  onKeyDown={e => handleItemKeyDown(e, idx)}
                />
              ))}
            </>
          ) : query?.length >= 3 ? (
            <If
              condition={isLoadingResults}
              then={<SingleRowListSlim rows={4} />}
              else={
                <div className="px-6 py-6 text-sm text-neutral-400">
                  No results for “{query}”. Try a name, email, NMLS or ID.
                </div>
              }
            />
          ) : (
            <RecentSearches
              recentSearches={recentSearchesData}
              onClickItem={onClickItem}
              onClickResetRecentSearches={onClickResetRecentSearches}
              itemRefs={itemRefs}
              activeIndex={activeIndex}
              onItemKeyDown={handleItemKeyDown}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchAnything
